import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { OverlayEventDetail } from '@ionic/core';
import { Store } from '@ngrx/store';
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';
import { EditAmountModalComponent } from '@payments/components/edit-amount-modal/edit-amount-modal.component';
import { ShortPayModalComponent } from '@payments/components/short-pay-modal/short-pay-modal.component';
import { AdjustPaymentPageViewModel } from '@payments/models/adjust-payment-page-view-model';
import {
  getAdjustPaymentViewModel,
  getFiltersAndSortInformation,
} from '@payments/selectors/payments.selector';
import { ModalFilterService } from '@payments/services/payments-modal.service';
import { PATHS } from '@shared/constants/paths';
import { FEATURES } from '@shared/constants/splitio-features';
import { ModalService } from '@shared/services/modal/modal.service';
import {
  Filter,
  InvoiceFilterIds,
  InvoiceStoreService,
  SortDirection,
  SortFilter,
  Transaction,
} from '@usf/ngrx-payment';
import {
  PaymentTransactionsViewModel,
  SortFields,
  Status,
} from '@usf/payment-types';
import { Observable, Subject, combineLatest, map } from 'rxjs';
import { filter, startWith } from 'rxjs/operators';

enum SelectedSortType {
  alphabetical,
  date,
  number,
}

enum SelectedSortTranslation {
  customerName = 'customerName',
  customerPO = 'customerPO',
  dueDate = 'dueDate',
  grossAmount = 'amount',
  issueDate = 'issueDate',
  netAmount = 'amountDue',
  status = 'status',
  transactionNumber = 'transactionNumber',
}

const selectedSorts = {
  amount: SelectedSortType.number,
  amountDue: SelectedSortType.number,
  customerName: SelectedSortType.alphabetical,
  customerPO: SelectedSortType.alphabetical,
  dueDate: SelectedSortType.date,
  issueDate: SelectedSortType.date,
  status: SelectedSortType.alphabetical,
  transactionNumber: SelectedSortType.number,
};

@Injectable({
  providedIn: 'root',
})
export class AdjustPaymentService implements ModalFilterService {
  readonly restrictToOpen = true;
  readonly defaultFilter = {
    fieldName: SortFields.dueDate,
    sortValue: SortDirection.ASC,
  };
  initialInvoiceSort: SortFilter[] = [
    this.defaultFilter,
    { fieldName: SortFields.transaction, sortValue: SortDirection.DESC },
  ];

  private excludeInvoices: string[] = [];
  private selectedSortFiltersSubject = new Subject<SortFilter[]>();
  private selectedSortFilters$ = this.selectedSortFiltersSubject.asObservable();

  constructor(
    private store: Store,
    private panAppState: PanAppState,
    private invoiceStoreService: InvoiceStoreService,
    private modalService: ModalService,
    public modalController: ModalController,
    readonly platformService: PlatformService,
    readonly router: Router,
  ) {}

  clearFiltersAndResetSort() {
    let newFilters = this.getExcludeInvoicesFilter();
    this.invoiceStoreService.setInvoiceFilters(newFilters, this.restrictToOpen);

    this.invoiceStoreService.setInvoiceSort(
      this.initialInvoiceSort,
      this.restrictToOpen,
    );
  }

  private getExcludeInvoicesFilter(): Filter[] {
    let newFilters: Filter[] = [];
    if (this.excludeInvoices.length > 0) {
      newFilters.push({
        filterName: InvoiceFilterIds.ExcludeInvoices,
        value: this.excludeInvoices,
      });
    }
    return newFilters;
  }

  getViewModel$(): Observable<AdjustPaymentPageViewModel> {
    const emptySelectedSortFilters$ = this.selectedSortFilters$.pipe(
      startWith(this.initialInvoiceSort),
    );
    return combineLatest([
      this.store.select(getAdjustPaymentViewModel),
      emptySelectedSortFilters$,
      this.panAppState.feature$([
        FEATURES.split_dynamic_payments_order_number.name,
      ]),
    ]).pipe(
      map(([vm, selectedSortFilters, orderNumberFlag]) => {
        const sortedInitialSelectedTransactions = this.sortTransactions(
          vm.initialSelectedTransactions,
          selectedSortFilters,
        );
        return {
          ...vm,
          initialSelectedTransactions: sortedInitialSelectedTransactions,
          showOrderNumber: orderNumberFlag,
        };
      }),
    );
  }

  setExcludeInvoices(excludeInvoices: string[]) {
    this.excludeInvoices = excludeInvoices;
  }

  setSelectedSort(sortOrder: SortFilter[]) {
    this.selectedSortFiltersSubject.next(sortOrder);
  }

  setSort(sortOrder: SortFilter[]) {
    this.invoiceStoreService.setInvoiceSort(sortOrder, this.restrictToOpen);
    if (this.platformService.platformType !== PlatformEnum.desktop) {
      this.setSelectedSort(sortOrder);
    }
  }

  async openShortPayValidationModal(): Promise<
    OverlayEventDetail<{ shortPayCode: string } | null>
  > {
    this.modalService.setModalOptions(
      this.platformService.isTouch.value,
      ShortPayModalComponent,
      {},
      'modal-desktop-size-lg',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );

    await modal.present();
    return await modal.onDidDismiss();
  }

  async openEditAmountValidationModal(
    transactionDetails: PaymentTransactionsViewModel,
  ): Promise<OverlayEventDetail<{ paymentAmount: number } | null>> {
    this.modalService.setModalOptions(
      true,
      EditAmountModalComponent,
      {
        transactionDetails: transactionDetails,
      },
      'modal-desktop-size-lg',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );

    await modal.present();
    return await modal.onDidDismiss();
  }

  setFilter(invoiceFilters: Filter[]) {
    const mergedInvoiceFilters = [
      ...invoiceFilters,
      ...this.getExcludeInvoicesFilter(),
    ];

    this.invoiceStoreService.setInvoiceFilters(
      mergedInvoiceFilters,
      this.restrictToOpen,
      true,
    );
    this.invoiceStoreService.setInitialSelectedInvoices();
  }

  setInitialSelectedInvoices() {
    this.invoiceStoreService.setInitialSelectedInvoices();
  }

  setInitialSortAndFilter(customers: number[], excludeInvoices: string[]) {
    this.excludeInvoices = excludeInvoices;

    let newFilters = this.getExcludeInvoicesFilter();
    if (customers.length > 0) {
      newFilters.push({
        filterName: InvoiceFilterIds.Customers,
        value: customers,
      });
    }

    this.invoiceStoreService.setInvoiceFilters(newFilters, this.restrictToOpen);

    this.invoiceStoreService.setInvoiceSort(
      this.initialInvoiceSort,
      this.restrictToOpen,
    );
  }

  private sortTransactions(
    transactions: PaymentTransactionsViewModel[],
    sortFilters: SortFilter[],
  ): PaymentTransactionsViewModel[] {
    let newTransactions = [...transactions];
    let mutableSortFilters = [...sortFilters];
    if (newTransactions.length > 0 && sortFilters.length > 0) {
      mutableSortFilters.reverse().forEach(sortFilter => {
        const fieldName = SelectedSortTranslation[sortFilter.fieldName];
        switch (selectedSorts[fieldName]) {
          case SelectedSortType.alphabetical:
            newTransactions.sort((a, b) =>
              a[fieldName]
                .toLowerCase()
                .localeCompare(b[fieldName].toLowerCase()),
            );
            break;
          default:
            newTransactions.sort((a, b) => a[fieldName] - b[fieldName]);
            break;
        }
        if (sortFilter.sortValue === SortDirection.DESC) {
          newTransactions.reverse();
        }
      });
    }
    return newTransactions;
  }

  updateInvoice(
    transaction: PaymentTransactionsViewModel,
    data: Partial<Transaction>,
  ) {
    this.invoiceStoreService.updateInvoice(transaction._id, data);
  }

  // ModalFilterService

  getFiltersAndSortInformation$(): Observable<{
    filters: Filter[];
    sort: SortFilter[];
  }> {
    return this.store.select(getFiltersAndSortInformation);
  }

  getStatusOptions(): Map<Status, string> {
    return new Map([
      [Status.OVERDUE, 'Overdue'],
      [Status.PARTIALLY_PAID, 'Partially Paid'],
      [Status.PENDING, 'Pending'],
      [Status.PROCESSING, 'Processing'],
      [Status.CANCELLING, 'Cancelling'],
      [Status.UNPAID, 'Unpaid'],
      [Status.UNUSED, 'Unused'],
      [Status.AUTOPAY, 'Autopay'],
    ]);
  }

  canActivate(): Observable<boolean> {
    return combineLatest([this.getViewModel$(), this.panAppState.user$]).pipe(
      filter(([, user]) => !!user),
      map(([vm]) => {
        const canAccess = !!vm.selectedTransactions.length;
        !canAccess && this.router.navigate([PATHS.PAYMENTS]);
        return canAccess;
      }),
    );
  }
}
